import { Component, computed, effect, input, signal } from '@angular/core';
import { getBrandLogoUrl, getSubBrandLogoUrl, BrandLogoVariant } from './brand-logo.util';

/**
 * Small presentational component that renders a brand's logo from the public
 * static CDN. Per AD-16, there is no fallback: if the file 404s the `<img>`
 * removes itself and the cell stays empty.
 */
@Component({
  selector: 'cmt-brand-logo',
  standalone: true,
  template: `
    @if (url() && visible()) {
      <img
        class="brand-logo-img"
        [src]="url()"
        [alt]="domain() || 'Brand logo'"
        [style.max-height]="maxHeight()"
        (error)="hide()"
      />
    }
  `,
  styles: [`
    :host { display: inline-flex; align-items: center; }
    .brand-logo-img {
      display: block;
      max-width: 100%;
      width: auto;
      height: auto;
      object-fit: contain;
    }
  `],
})
export class BrandLogoComponent {
  readonly domain = input<string | null | undefined>(null);
  readonly variant = input<BrandLogoVariant>('thumb');
  /** When set, renders the SUB-brand logo (sub-folder of the parent `domain`). */
  readonly subBrand = input<string | null | undefined>(null);

  /** Optional override. Defaults: thumb 2rem, hero 3.5rem. */
  readonly heightOverride = input<string | null>(null);

  protected readonly visible = signal(true);

  protected readonly url = computed(() => {
    const sub = this.subBrand();
    return sub
      ? getSubBrandLogoUrl(this.domain(), sub, this.variant())
      : getBrandLogoUrl(this.domain(), this.variant());
  });

  protected readonly maxHeight = computed(() => {
    const override = this.heightOverride();
    if (override) return override;
    return this.variant() === 'hero' ? '3.5rem' : '2rem';
  });

  constructor() {
    effect(() => {
      // Re-show whenever the source URL changes (e.g. brand swap via @Input).
      this.url();
      this.visible.set(true);
    });
  }

  protected hide(): void {
    this.visible.set(false);
  }
}
